/**
 * DateSpan — renders a horizontal bar spanning multiple day cells for cards
 * that have both a startDate and dueDate. Splits into segments at week-row
 * boundaries and clamps to the visible grid range.
 *
 * Validates: Requirements 4.1, 4.3, 4.4, 5.4, 10.4, 10.6
 */

import React, { useCallback, useMemo } from 'react';
import type { DateSpanData } from '../../utils/date-utils';
import { daysBetween, formatDateForAria } from '../../utils/date-utils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateSpanProps {
  readonly span: DateSpanData;
  readonly visibleStart: string; // first visible date in grid ('YYYY-MM-DD')
  readonly visibleEnd: string;   // last visible date in grid ('YYYY-MM-DD')
  readonly onClick: () => void;
}

/** A single rendered segment of a date span (one per week row). */
interface SpanSegment {
  /** 1-based column start within the 7-column week grid. */
  readonly gridColumnStart: number;
  /** Number of columns this segment spans. */
  readonly gridColumnSpan: number;
  /** Which week row (0-based) this segment belongs to. */
  readonly weekRow: number;
  /** Whether this is the first segment (rounded left end). */
  readonly isFirst: boolean;
  /** Whether this is the last segment (rounded right end). */
  readonly isLast: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse a 'YYYY-MM-DD' string into a local Date at midnight. */
function parseDate(dateStr: string): Date {
  const parts = dateStr.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]) - 1;
  const day = Number(parts[2]);
  return new Date(year, month, day);
}

/**
 * Compute segments for a date span clamped to the visible range.
 * Each segment represents a contiguous run within a single week row.
 * Segments split at Sunday boundaries (start of new week row).
 */
function computeSegments(
  spanStart: string,
  spanEnd: string,
  visibleStart: string,
  visibleEnd: string,
): SpanSegment[] {
  // Clamp to visible range
  const clampedStart = spanStart > visibleStart ? spanStart : visibleStart;
  const clampedEnd = spanEnd < visibleEnd ? spanEnd : visibleEnd;

  // If entirely outside visible range, render nothing
  if (clampedStart > clampedEnd) {
    return [];
  }

  const startDate = parseDate(clampedStart);
  const visibleStartDate = parseDate(visibleStart);

  // Calculate offset of clamped start from the visible grid start (in days)
  const startOffset = Math.round(
    (startDate.getTime() - visibleStartDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  const totalDays = daysBetween(clampedStart, clampedEnd);
  const segments: SpanSegment[] = [];

  let daysRemaining = totalDays;
  let currentOffset = startOffset;

  while (daysRemaining > 0) {
    // Determine which week row and column position
    const weekRow = Math.floor(currentOffset / 7);
    const columnInWeek = currentOffset % 7; // 0-based column (0=Sun)
    const gridColumnStart = columnInWeek + 1; // 1-based for CSS Grid

    // Days left in this week row from current position
    const daysLeftInWeek = 7 - columnInWeek;
    const segmentDays = Math.min(daysRemaining, daysLeftInWeek);

    segments.push({
      gridColumnStart,
      gridColumnSpan: segmentDays,
      weekRow,
      isFirst: segments.length === 0,
      isLast: daysRemaining - segmentDays === 0,
    });

    daysRemaining -= segmentDays;
    currentOffset += segmentDays;
  }

  return segments;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DateSpan({
  span,
  visibleStart,
  visibleEnd,
  onClick,
}: DateSpanProps): React.ReactElement | null {
  const segments = useMemo(
    () => computeSegments(span.startDate, span.endDate, visibleStart, visibleEnd),
    [span.startDate, span.endDate, visibleStart, visibleEnd],
  );

  const ariaLabel = `${span.card.title}, ${formatDateForAria(span.startDate)} to ${formatDateForAria(span.endDate)}`;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  if (segments.length === 0) {
    return null;
  }

  return (
    <>
      {segments.map((segment, index) => (
        <div
          key={`${span.card.cardId}-seg-${index}`}
          role="button"
          tabIndex={0}
          aria-label={ariaLabel}
          className={`
            pointer-events-auto
            flex items-center px-1.5 py-0.5 text-xs cursor-pointer select-none
            hover:brightness-90 focus:outline-none focus:ring-2 focus:ring-blue-500
            transition-colors truncate
            ${segment.isFirst ? 'rounded-l' : ''}
            ${segment.isLast ? 'rounded-r' : ''}
          `.trim()}
          style={{
            gridColumn: `${segment.gridColumnStart} / span ${segment.gridColumnSpan}`,
            backgroundColor: `${span.listColor}33`, // ~20% opacity
            borderLeft: segment.isFirst ? `3px solid ${span.listColor}` : undefined,
            color: span.listColor,
          }}
          onClick={onClick}
          onKeyDown={handleKeyDown}
        >
          {segment.isFirst && (
            <span className="truncate font-medium">{span.card.title}</span>
          )}
        </div>
      ))}
    </>
  );
}
